/**
 * absoluteMediaUrl — force any `/api/...` relative path returned by the
 * backend onto the explicit API origin (current window origin in
 * production, REACT_APP_BACKEND_URL only when running on localhost).
 *
 * Why this exists (May 01 2026 production bug):
 *   On the Emergent preview, frontend + backend share an origin so a
 *   relative `/api/cinematic/asset/xyz.mp3` resolves to the right place.
 *   On a deployed custom domain (sharedorigins.org), the frontend lives
 *   on that domain but the backend may have been baked with a different
 *   URL. A relative path then 404s if the user's origin doesn't carry
 *   the /api ingress route, OR a baked absolute URL trips CORS.
 *
 * Rules:
 *   - `null`/undefined/empty → returned as-is (caller decides fallback)
 *   - already-absolute (http/https/data:/blob:) → returned unchanged
 *   - starts with `/api/` → prepend the resolved API base
 *   - anything else → returned as-is (static assets, cdn, etc.)
 */
function _resolveBase() {
  const envValue = process.env.REACT_APP_BACKEND_URL || '';
  if (typeof window === 'undefined' || !window.location || !window.location.origin) {
    return envValue;
  }
  const here = window.location.origin;
  if (!envValue || envValue === here) return here || envValue;
  const isLocalHere = /^https?:\/\/(localhost|127\.\d+\.\d+\.\d+|0\.0\.0\.0|192\.168\.|10\.|\[::1\])/i.test(here);
  return isLocalHere ? envValue : here;
}

/** Public helper: resolved API base origin (no trailing /api). */
export function apiBase() {
  return _resolveBase();
}

export default function absoluteMediaUrl(url) {
  if (!url || typeof url !== 'string') return url;
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  if (url.startsWith('/api/')) return `${_resolveBase()}${url}`;
  return url;
}
